import React, { useState, useEffect } from 'react';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import axios from 'axios';
import './styles.css';
import PhoneNumberInput from './PhoneNumberInput';
import MoneyFormat from './MoneyFormat';
import ScheduleBuilder from './ScheduleBuilder';
import TimePicker from './TimePicker';
import GroupedSelect from './GroupedSelect';
import Experience from './Experience';
import EmployeeList from './EmployeeList';
import PositionSelector from './PositionSelector';
import UserAdmin from './UserAdmin';
import Sidebar from './Sidebar';
import Footer from './Footer';
import Pizza from './pizza.svg';
import { DAYS, POSITION_GROUPS, GLOBAL_EXP_GROUPS } from '../../constants';
import { API_BASE } from './config';

// Someone has the old Render URL bookmarked
if (typeof window !== 'undefined' && window.location.hostname.endsWith('.onrender.com')) {
    window.location.replace(
        `https://davannischeduler.com${window.location.pathname}${window.location.search}`
    );
}

const App = () => {
    const [currentUser, setCurrentUser] = useState(null);
    const [authChecked, setAuthChecked] = useState(false);
    const [activeComponent, setActiveComponent] = useState('employeeList');
    const [schedule, setSchedule] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [positions, setPositions] = useState([]);
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState('');

    const { register, handleSubmit, reset, setValue, watch, control, formState: { errors } } = useForm();

    const [availability, setAvailability] = useState({
        Monday: { startTime: '', endTime: '' },
        Tuesday: { startTime: '', endTime: '' },
        Wednesday: { startTime: '', endTime: '' },
        Thursday: { startTime: '', endTime: '' },
        Friday: { startTime: '', endTime: '' },
        Saturday: { startTime: '', endTime: '' },
        Sunday: { startTime: '', endTime: '' }
    });

    const [modalOpen, setModalOpen] = useState(false);
    const [editingEmployee, setEditingEmployee] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    const watchedGroup = watch('group', 'Manager');

    axios.defaults.withCredentials = true;

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const response = await axios.get(`${API_BASE}/api/auth/me`);
                setCurrentUser(response.data);
            } catch (err) {
                setCurrentUser(null);
            } finally {
                setAuthChecked(true);
            }
        };
        checkAuth();
    }, []);

    const loadStores = async () => {
        try {
            const response = await axios.get(`${API_BASE}/api/stores`);
            setStores(response.data);
            if (response.data.length > 0 && !selectedStore) {
                setSelectedStore(response.data[0]._id);
            }
        } catch (err) {
            console.error('Error loading stores:', err);
        }
    };

    useEffect(() => {
        if (!currentUser) return;
        loadStores();
    }, [currentUser]);

    const handleLogout = async () => {
        try {
            await axios.post(`${API_BASE}/api/auth/logout`);
            setCurrentUser(null);
        } catch (err) {
            console.error('Logout failed:', err);
        }
    };

    const onSubmit = async (data) => {
        if (submitting) return;
        setSubmitting(true);

        const newEmployee = {
            name: data.name,
            rate: data.rate,
            phoneNumber: data.phoneNumber,
            group: data.group || 'Manager',
            exp: GLOBAL_EXP_GROUPS.includes(data.group) ? (data.exp || '1') : undefined,
            canExceed30hrs: data.canExceed30hrs || false,
            locations: editingEmployee?.locations || [selectedStore],
            positions: POSITION_GROUPS.includes(data.group) ? positions : [],
            availability: Object.keys(availability)
                .filter(day => availability[day].startTime && availability[day].endTime)
                .map(day => ({
                    day,
                    startTime: availability[day].startTime || 'Not Available',
                    endTime: availability[day].endTime || 'Not Available',
                    notes: availability[day].notes || '',
                })),
        };

        DAYS.forEach(day => {
            if (!newEmployee.availability.some(a => a.day === day)) {
                newEmployee.availability.push({
                    day,
                    startTime: 'Not Available',
                    endTime: 'Not Available',
                    notes: availability[day]?.notes || '',
                });
            }
        });

        try {
            if (editingEmployee) {
                const response = await axios.put(`${API_BASE}/api/employees/${editingEmployee._id}`, newEmployee);
                setEmployees(prevEmployees =>
                    prevEmployees.map(emp =>
                        emp._id === editingEmployee._id ? response.data : emp
                    )
                );
            } else {
                const response = await axios.post(`${API_BASE}/api/employees`, newEmployee);
                setEmployees(prevEmployees => [...prevEmployees, response.data]);
            }

            reset({ name: '', phoneNumber: '', group: 'Manager', exp: '1', canExceed30hrs: false });
            setAvailability({
                Monday: { startTime: '', endTime: '' },
                Tuesday: { startTime: '', endTime: '' },
                Wednesday: { startTime: '', endTime: '' },
                Thursday: { startTime: '', endTime: '' },
                Friday: { startTime: '', endTime: '' },
                Saturday: { startTime: '', endTime: '' },
                Sunday: { startTime: '', endTime: '' }
            });
            setPositions([]);
            setModalOpen(false);
        } catch (error) {
            console.error('Error adding/editing employee:', error);
            alert(error.response?.data?.message || 'Error saving employee');
        } finally {
            setSubmitting(false);
        }
    };

    const copyTimes = (fromDay, toDay) => {
        setAvailability(prev => ({
            ...prev,
            [toDay]: {
                ...prev[toDay],
                startTime: prev[fromDay].startTime,
                endTime: prev[fromDay].endTime
            }
        }));
    };

    const copyTimesToAllDays = (fromDay) => {
        setAvailability(prev => {
            const next = { ...prev };
            Object.keys(prev).forEach(day => {
                if (day === fromDay) return;
                next[day] = {
                    ...prev[day],
                    startTime: prev[fromDay].startTime,
                    endTime: prev[fromDay].endTime
                };
            });
            return next;
        });
    };

    const handleAddEmployee = () => {
        setEditingEmployee(null);
        reset({ name: '', rate: '', phoneNumber: '', group: 'Manager', exp: '1', canExceed30hrs: false });
        setAvailability({
            Monday: { startTime: '', endTime: '' },
            Tuesday: { startTime: '', endTime: '' },
            Wednesday: { startTime: '', endTime: '' },
            Thursday: { startTime: '', endTime: '' },
            Friday: { startTime: '', endTime: '' },
            Saturday: { startTime: '', endTime: '' },
            Sunday: { startTime: '', endTime: '' }
        });
        setPositions([]);
        setModalOpen(true);
    };

    const handleEdit = (employee) => {
        setEditingEmployee(employee);
        setValue('name', employee.name);
        setValue('rate', employee.rate);
        setValue('phoneNumber', employee.phoneNumber);
        setValue('group', employee.group);
        setValue('exp', employee.exp);
        setValue('canExceed30hrs', employee.canExceed30hrs || false);
        setPositions(employee.positions || []);
        employee.availability.forEach(a => {
            setAvailability(prev => ({
                ...prev,
                [a.day]: {
                    startTime: a.startTime,
                    endTime: a.endTime,
                    notes: a.notes || ''
                }
            }));
        });
        setModalOpen(true);
    };

    const handleGenerateSchedule = async () => {
        if (!selectedWeek) {
            alert('Please select a week first');
            return;
        }
        try {
            const response = await axios.post(`${API_BASE}/api/generate-schedule`, {
                weekStart: selectedWeek
            });
            setSchedule(response.data);
        } catch (error) {
            console.error('Error generating schedule:', error);
            alert('Error generating schedule. Please try again.');
        }
    };

    const renderComponent = () => {
       
        switch (activeComponent) {
            
            case 'employeeList':
                return (
                    <>
                        <button className="btn-lg" onClick={handleAddEmployee}>
                            Add Employee
                        </button>
                        <div>
                            <h2>Employee Availability</h2>
                            <EmployeeList
                                employees={employees}
                                setEmployees={setEmployees}
                                selectedStore={selectedStore}
                                stores={stores}
                                onEdit={handleEdit}
                            />
                        </div>
                    </>
                );
            case 'ScheduleBuilder':
                return <ScheduleBuilder selectedStore={selectedStore}
                />;
            case 'admin':
                return <UserAdmin stores={stores} currentUser={currentUser} onStoresChange={loadStores} />;
            default:
                return <div>Sorry, nothing to see here</div>;
        }
    };

    // loader
    const [timeElapsed, setTimeElapsed] = useState(false);
    const [showLoader, setShowLoader] = useState(true);

    useEffect(() => {
    const timer = setTimeout(() => {
        setTimeElapsed(true);
    }, 1200);
    return () => clearTimeout(timer);
    }, []);

    const isExiting = authChecked && timeElapsed;

    if (showLoader) {
    return (
        <div 
        className={`app-loading ${isExiting ? 'fade-out' : ''}`}
        onAnimationEnd={() => {
            setShowLoader(false); 
        }}
        >
        <img className="pizza" src={Pizza} alt="Loading..."/>
        </div>
    );
    }

    if (!currentUser) {
        const denied = new URLSearchParams(window.location.search).get('auth') === 'denied';

        return (
            <div className="login-screen">
                <h1>Scheduler</h1>
                {denied && (
                    <p className="error-message">
                        That account doesn't have access. Contact an administrator.
                    </p>
                )}
                <a className="btn-lg line-button" href={`${API_BASE}/api/auth/google`}>
                    Sign in with Google
                </a>
            </div>
        );
    }

    return (
        <><div className="app-container">
            <Sidebar
                activeComponent={activeComponent}
                setActiveComponent={setActiveComponent}
                stores={stores}
                selectedStore={selectedStore}
                setSelectedStore={setSelectedStore}
                currentUser={currentUser}
                onLogout={handleLogout} />


            <div className="content">
                {renderComponent()}

                {modalOpen && (
                    <div className="modal-overlay">
                        <div className="modal">
                            <h2>{editingEmployee ? 'Edit Employee' : 'Add Employee'}</h2>
                            <FormProvider {...{ register, handleSubmit, reset, formState: { errors } }}>
                                <form onSubmit={handleSubmit(onSubmit)}>

                                    <div className="form-section-basic">

                                        <label>Name
                                            <input
                                                type="text"
                                                {...register('name', { required: 'Name is required' })}
                                                placeholder="Name" />
                                            {errors.name && <span className="field-error">{errors.name.message}</span>}
                                        </label>

                                        <label>Rate
                                            <Controller
                                                name="rate"
                                                control={control}
                                                rules={{
                                                    required: 'Rate is required',
                                                    validate: (value) => {
                                                        const num = parseFloat(value);
                                                        if (isNaN(num)) return 'Enter a valid amount';
                                                        if (num <= 0) return 'Rate must be greater than zero';
                                                        if (num > 10000) return 'Rate cannot exceed $10,000';
                                                        return true;
                                                    }
                                                }}
                                                render={({ field }) => (
                                                    <MoneyFormat
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        placeholder="$0.00" />
                                                )} />
                                            {errors.rate && <span className="field-error">{errors.rate.message}</span>}
                                        </label>

                                        <label>Phone
                                            <Controller
                                                name="phoneNumber"
                                                control={control}
                                                rules={{
                                                    required: 'Phone Number is required',
                                                    validate: (value) => {
                                                        const digits = value?.replace(/[-_]/g, '') || '';
                                                        return digits.length === 10 || 'Phone number must be 10 digits';
                                                    }
                                                }}
                                                render={({ field }) => (
                                                    <PhoneNumberInput
                                                        value={field.value}
                                                        onChange={field.onChange}
                                                        placeholder="___-___-____" />
                                                )} />
                                            {errors.phoneNumber && <span className="field-error">{errors.phoneNumber.message}</span>}
                                        </label>

                                        <label>Group
                                            <GroupedSelect
                                                register={register}
                                                errors={errors}
                                                defaultValue="Manager" />
                                        </label>

                                        {GLOBAL_EXP_GROUPS.includes(watchedGroup) && (
                                            <label>Exp
                                                <Experience
                                                    register={register}
                                                    errors={errors}
                                                    defaultValue="1" />
                                            </label>
                                        )}

                                        {POSITION_GROUPS.includes(watchedGroup) && (
                                            <PositionSelector
                                                positions={positions}
                                                setPositions={setPositions} />
                                        )}

                                        <label className="checkbox-label">
                                            <input type="checkbox" {...register('canExceed30hrs')} />
                                            Over 30 hrs?
                                        </label>

                                    </div>

                                    <div className="form-section-availability">
                                        <div className="copy-arrows">
                                            <button
                                                type="button"
                                                className="copy-next savebtn"
                                                onClick={() => copyTimes('Sunday', 'Monday')}
                                                disabled={!availability.Sunday?.startTime}
                                                title="Copy Sunday's times to Monday"
                                            >
                                                →
                                            </button>
                                            <button
                                                type="button"
                                                className="copy-next savebtn"
                                                onClick={() => copyTimes('Monday', 'Sunday')}
                                                disabled={!availability.Monday?.startTime}
                                                title="Copy Monday's times to Sunday"
                                            >
                                                ←
                                            </button>
                                        </div>

                                        {Object.keys(availability).map((day, index, days) => (
                                            <React.Fragment key={day}>
                                                <div className="day-container">
                                                    <h4>{day}</h4>
                                                    <TimePicker
                                                        day={day}
                                                        availability={availability}
                                                        handleAvailabilityChange={(day, field, value) => setAvailability(prev => ({
                                                            ...prev,
                                                            [day]: {
                                                                ...prev[day],
                                                                [field]: value
                                                            }
                                                        }))} />
                                                    <label>Notes
                                                        <input
                                                            type="text"
                                                            value={availability[day]?.notes || ''}
                                                            onChange={(e) => setAvailability(prev => ({
                                                                ...prev,
                                                                [day]: {
                                                                    ...prev[day],
                                                                    notes: e.target.value
                                                                }
                                                            }))}
                                                            placeholder="Prep...Manage..." />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        className="copy-all savebtn"
                                                        onClick={() => copyTimesToAllDays(day)}
                                                        disabled={!availability[day]?.startTime}
                                                        title={`Copy ${day}'s times to every other day`}
                                                    >
                                                        Copy to every day
                                                    </button>
                                                </div>

                                                {index < days.length - 1 && (
                                                    <div className="copy-arrows">
                                                        <button
                                                            type="button"
                                                            className="copy-next savebtn"
                                                            onClick={() => copyTimes(day, days[index + 1])}
                                                            disabled={!availability[day]?.startTime}
                                                            title={`Copy ${day}'s times to ${days[index + 1]}`}
                                                        >
                                                            →
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="copy-next savebtn"
                                                            onClick={() => copyTimes(days[index + 1], day)}
                                                            disabled={!availability[days[index + 1]]?.startTime}
                                                            title={`Copy ${days[index + 1]}'s times to ${day}`}
                                                        >
                                                            ←
                                                        </button>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        ))}
                                        <div className="copy-arrows">
                                            <button
                                                type="button"
                                                className="copy-next savebtn"
                                                onClick={() => copyTimes('Sunday', 'Monday')}
                                                disabled={!availability.Sunday?.startTime}
                                                title="Copy Sunday's times to Monday"
                                            >
                                                →
                                            </button>
                                            <button
                                                type="button"
                                                className="copy-next savebtn"
                                                onClick={() => copyTimes('Monday', 'Sunday')}
                                                disabled={!availability.Monday?.startTime}
                                                title="Copy Monday's times to Sunday"
                                            >
                                                ←
                                            </button>
                                        </div>
                                    </div>

                                    {/* ── Actions ── */}
                                    <div className="form-section-actions">
                                        <button className="updatebtn" type="submit" disabled={submitting}>
                                            {submitting ? 'Saving…' : (editingEmployee ? 'Update' : 'Add Employee')}
                                        </button>
                                        <button type="button" onClick={() => setModalOpen(false)}>Cancel</button>
                                    </div>

                                </form>
                            </FormProvider>
                        </div>
                    </div>
                )}

            </div>
        </div>
        <Footer>

        </Footer></>
    );
};

export default App;